import React, { useContext, useState } from 'react'
import { QueryObserverResult, RefetchOptions } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { Box, Button, Icon } from '@node-space/ui-components'
import { useProfileContext } from 'hooks/context/useProfileContext'
import { useFeatureFlags } from 'hooks/useFeatureFlags'
import { useIsSuspendedAccount } from 'hooks/useIsSuspendedAccount'
import { useMappedRoles } from 'hooks/useMappedRoles'
import { WalletSheet } from 'pages/Wallets/components/Sheet/WalletSheet'
import { WalletActionContext } from 'pages/Wallets/context/WalletSheetProvider'
import { ActionIds } from 'types/types'
import {
  AmplitudeEvent,
  AmplitudeEventAction,
  AmplitudeEventCategory,
} from 'utils/amplitude/amplitudeEvents'
import track from 'utils/tracker'

interface CryptoPaymentsHeaderButtonsProps {
  refetch?: (options?: RefetchOptions) => Promise<QueryObserverResult<unknown, Error>>
}

export const CryptoPaymentsHeaderButtons = ({ refetch }: CryptoPaymentsHeaderButtonsProps) => {
  const { t } = useTranslation()
  const { viewOnly } = useProfileContext()
  const { enableCryptoFiatSend } = useFeatureFlags()
  const permissions = useMappedRoles()?.payments
  const canCreatePayIn = permissions?.canCreatePayIn
  const canCreatePayOut = permissions?.canCreatePayOut
  const [, setWalletAction] = useContext(WalletActionContext)
  const [isSheetOpen, setIsSheetOpen] = useState(false)
  const isSuspendedAccount = useIsSuspendedAccount()

  const handleOpenSendSheet = () => {
    setWalletAction({
      actionId: ActionIds?.SEND,
      header: t('wallets.send.contactStepHeader'),
      onClose: () => {
        setIsSheetOpen(false)
      },
      meta: {
        onPayoutSuccess: refetch,
      },
    })
    setIsSheetOpen(true)
  }

  const handleOpenPaymentLinkSheet = () => {
    track.Amp.track(AmplitudeEvent.PAYMENT_INITIATE, {
      category: AmplitudeEventCategory.MERCHANT_PORTAL,
      action: AmplitudeEventAction.CLICK,
    })
    setWalletAction({
      actionId: ActionIds?.PAYMENT_LINK,
      header: t('payments.paymentLinks.contactStepHeader'),
      onClose: () => {
        setIsSheetOpen(false)
      },
      meta: {
        onPaymentLinkSuccess: refetch,
      },
    })
    setIsSheetOpen(true)
  }

  return (
    <>
      <Box flex gapX={12}>
        <Button
          testId="request-crypto"
          disabled={!canCreatePayIn || viewOnly || isSuspendedAccount}
          onClick={handleOpenPaymentLinkSheet}
          iconElement={<Icon name="ArrowDownLeft" />}
        >
          {t('payments.requestCrypto')}
        </Button>
        {canCreatePayOut && (
          <Button
            testId="send-crypto"
            disabled={viewOnly || isSuspendedAccount}
            onClick={handleOpenSendSheet}
            iconElement={<Icon name="ArrowUpRight" />}
          >
            {enableCryptoFiatSend ? t('transactionAction.send') : t('payments.sendCrypto')}
          </Button>
        )}
      </Box>

      <WalletSheet isSheetOpen={isSheetOpen} />
    </>
  )
}
